import { useMemo } from "react";

import type { ValueMap } from "../types";

import { useCompetitorsFilterStore } from "./useCompetitorsFilterStore";
import { useCompetitorsMap } from "./useCompetitorsMap";

interface IUseEffectiveSelectedCompetitorsOptions {
    /**
     * When true, drops secondary-domain entries from the returned selection.
     * Used by tabs whose backend only supports primary competitors (Ads, SEO).
     */
    primaryCompetitorsOnly?: boolean;
}

/** Stored competitor selection intersected with the currently available options. */
export const useEffectiveSelectedCompetitors = ({
    primaryCompetitorsOnly = false,
}: IUseEffectiveSelectedCompetitorsOptions = {}) => {
    const { allCompetitorsValues } = useCompetitorsMap();
    const selectedCompetitors = useCompetitorsFilterStore((state) => state.selectedCompetitors);

    return useMemo(() => {
        const map: ValueMap = new Map();
        selectedCompetitors.forEach((value, key) => {
            const available = allCompetitorsValues.get(key);
            if (!available) {
                return;
            }
            if (primaryCompetitorsOnly && available.kind === "secondary") {
                return;
            }
            map.set(key, value);
        });
        return {
            selectedCompetitors: map,
            selectedCompetitorIds: Array.from(map.keys()),
        };
    }, [allCompetitorsValues, selectedCompetitors, primaryCompetitorsOnly]);
};
